import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import api from "api/api";
import endpoint from "api/endpoints";

/**
 * Every brand that has at least one visible product, `ORDER BY b.name`.
 *
 * The endpoint is `GET_ALL_UNIQUE_BRANDS`, which joins `item` to
 * `CUSTOMLIST_DND_WEB_BRANDS` and applies the item-visibility conditions, so an
 * offline/inactive-only brand never appears. It is **not** `GET_ALL_BRANDS`, which
 * returns the raw customlist including brands nothing is stocked under.
 *
 * Rows are `{ brand_id, brand_name }`. There is no count, tag or logo column —
 * NetSuite's brand customlist holds an id and a name and nothing else.
 *
 * `ListProductComponent` calls the same endpoint directly for the filter rail rather
 * than reading this slice, so on `/products` the request is made twice. Both hit the
 * backend's `TTL.REFERENCE` (6h) cache, so the second is cheap; consolidating them is
 * worth doing when that component is next touched.
 */
export const fetchBrands = createAsyncThunk(
  "brand/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(endpoint.GET_ALL_UNIQUE_BRANDS);
      const rows = Array.isArray(res.data) ? res.data : res.data?.items || [];

      // Drop SuiteQL's per-row `links` array, as classificationSlice does.
      return rows.map(({ links, ...rest }) => rest);
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

const brandSlice = createSlice({
  name: "brand",
  initialState: {
    brands: [], // Array of { brand_id, brand_name }
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBrands.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.loading = false;
        state.brands = action.payload;
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default brandSlice.reducer;
